import { sum, uniq } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { getComponentDesignQuantities, getFlattenedChildrenWithCalculatedQuantity } from '@paragon/layout-models';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { RootState } from '../index';
import { getActiveStation } from './getActiveStation';
import { getProjectLayoutModel } from './getProjectLayoutModel';
import { getActiveJobLayoutModel } from './jobContextSelectors';
import { getOrderedProductionComponentInstancesForActiveScheduleLineGroup } from './scheduleStationSelectors';

export const getContextTotals = createSelector(
  (state: RootState) => state.application.openContext,
  getProjectLayoutModel,
  getFlattenedChildrenWithCalculatedQuantity,
  getActiveJobLayoutModel,
  (state: RootState) => state.sharedComponentSet.sharedComponentSet,
  getActiveStation,
  (state: RootState) => state.productionModels,
  getOrderedProductionComponentInstancesForActiveScheduleLineGroup,

  (
    openContext,
    projectLayoutModel,
    flattenedChildrenWithQuantityGetter,
    jobLayoutModel,
    sharedComponentSet,
    activeStation,
    productionModels,
    instancesForActiveScheduleLineGroup,
  ): { unique: number; total: number | null } | null => {
    if (!openContext) return null;

    const openContextType = openContext.type;
    switch (openContextType) {
      case 'project': {
        if (!projectLayoutModel) return null;
        const componentDesignQuantitiesFromProject = getComponentDesignQuantities(
          flattenedChildrenWithQuantityGetter(projectLayoutModel.guid),
        );

        const total = sum(Object.values(componentDesignQuantitiesFromProject));
        const unique = Object.keys(componentDesignQuantitiesFromProject).length;
        return { unique, total };
      }

      case 'job': {
        return jobLayoutModel ? { unique: jobLayoutModel.componentDesignGuids.length, total: null } : null;
      }

      case 'shared-component-set': {
        if (!sharedComponentSet) return null;
        const total = sum(sharedComponentSet.analysisSetGuidsWithQuantities.map(({ quantity }) => quantity));
        const unique = sharedComponentSet.analysisSetGuidsWithQuantities.length;
        return { unique, total };
      }

      case 'station': {
        if (!activeStation) return null;
        const unique = Object.keys(activeStation.componentDesigns).length;
        const total = sum(
          Object.values(activeStation.componentDesigns).map(
            (stationComponentDesign) => stationComponentDesign.quantity ?? 0,
          ),
        );

        return { unique, total };
      }

      case 'schedule-station': {
        if (!productionModels.activeScheduleLineGroupGuid) return null;
        const scheduleLineGroup = productionModels.orderedScheduleLineGroups.find(
          (scheduleLineGroup) => scheduleLineGroup.guid === productionModels.activeScheduleLineGroupGuid,
        );
        if (!scheduleLineGroup) return null;

        const unique = uniq(instancesForActiveScheduleLineGroup.map((instance) => instance.analysisSetGuid)).length;
        const total = instancesForActiveScheduleLineGroup.length;
        return { unique, total };
      }

      default:
        assertExhaustiveSwitch(openContextType);
    }
  },
);
